import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brain, Box, Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';
import { trackSignup } from '../lib/analytics';
import GoogleAuthButton from '../components/GoogleAuthButton';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePassword = () => {
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    const result = await register(formData.email, formData.password, formData.fullName);

    if (result.success) {
      // Await the pixel flush before redirecting — without this the
      // CompleteRegistration beacon gets cancelled by the immediate
      // navigate() and never reaches Meta.
      await trackSignup('email');
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4 py-12">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://printmindai.com/' },
          { name: 'Sign Up', url: 'https://printmindai.com/register' },
        ]}
      />
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="relative">
              <Brain className="w-10 h-10 text-primary" />
              <Box className="w-5 h-5 text-accent absolute -bottom-1 -right-1" />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              PrintMind AI
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Create Your Account</h2>
          <p className="text-text-secondary">Start optimizing your 3D prints today</p>
        </div>

        {/* Register Form */}
        <div className="bg-bg-light rounded-xl p-8 border border-text-muted/20">
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {/* Google sign-up — most popular path first */}
          <GoogleAuthButton label="Sign up with Google" testId="register-google-btn" />

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-text-muted/15" />
            <span className="text-xs uppercase tracking-wider text-text-muted">or sign up with email</span>
            <div className="flex-1 h-px bg-text-muted/15" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white placeholder-text-muted focus:outline-none focus:border-primary transition"
                  placeholder="John Doe"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white placeholder-text-muted focus:outline-none focus:border-primary transition"
                  placeholder="your@email.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white placeholder-text-muted focus:outline-none focus:border-primary transition"
                  placeholder="At least 8 characters"
                  required
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white placeholder-text-muted focus:outline-none focus:border-primary transition"
                  placeholder="Confirm your password"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="w-4 h-4 mt-1 rounded border-text-muted/20 bg-bg-dark text-primary focus:ring-primary focus:ring-offset-0"
                data-testid="register-terms-checkbox"
              />
              <label className="ml-2 text-sm text-text-secondary">
                I agree to the{' '}
                <a href="#" className="text-primary hover:text-primary-dark">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:text-primary-dark">Privacy Policy</a>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-text-muted/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-bg-light text-text-muted">Free plan includes</span>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-accent mr-2" />
                3 STL analyses per month
              </div>
              <div className="flex items-center text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-accent mr-2" />
                10 AI chat messages
              </div>
              <div className="flex items-center text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-accent mr-2" />
                Smart printer profiles
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-text-secondary text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:text-primary-dark font-semibold transition">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-text-muted hover:text-text-secondary text-sm transition">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
